import { useContext, useCallback, useEffect, useRef, useState } from 'react';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  TextL,
} from 'app/components';
import { ButtonsArea, SimulationStepContainer } from './styles';
import { ApplicationRequestContext } from '../ApplicationRequest';
import {
  CreditSimulator,
  CreditSimulatorRefs,
} from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { ConsigneeOrganType } from '../ConsignedStep/interfaces';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { IElegibility, IElegibilityConfigs } from './CreditSimulatorV2/types';
import { formatCurrency } from 'app/utils/normalizer';
import { TSimulatorV2Response } from 'app/services/simulatorHttpServiceV2';
import {
  defaultLoanGuaranteeFormValues,
  getFgtsGuaranteeExpectedFinancedAmount,
  getGuaranteeFinancedValidation,
  shouldUseMarginWithFgtsCoverageValidation,
  TGuaranteeFinancedValidationResult,
  TLoanGuaranteeFormValues,
} from '../ConsultaFgts/interfaces';

type SimulationV2StepProps = {
  isCalcSimulation?: boolean;
};

const MARGIN_RATIO_ERROR_MESSAGE =
  'O valor da margem precisa atingir ao menos 50% do valor do empréstimo.';

const getFgtsGuaranteeSumErrorMessage = (
  formValues: TLoanGuaranteeFormValues,
  financedAmount?: number,
) => {
  const expectedAmount = getFgtsGuaranteeExpectedFinancedAmount(financedAmount);
  const formattedExpected = formatCurrency(expectedAmount);

  if (
    formValues.hasFgtsBalanceGuarantee &&
    formValues.hasFgtsRescissionPenalty
  ) {
    return `A soma do Saldo FGTS e da Multa rescisória deve corresponder a 50% (${formattedExpected}) do valor do empréstimo.`;
  }

  if (formValues.hasFgtsBalanceGuarantee) {
    return `O valor do Saldo FGTS deve corresponder a 50% (${formattedExpected}) do valor do empréstimo.`;
  }

  return `O valor da Multa rescisória associada ao FGTS deve corresponder a 50% (${formattedExpected}) do valor do empréstimo.`;
};

const getGuaranteeValidationParams = (
  simulation: TSimulatorV2Response,
  formValues: TLoanGuaranteeFormValues,
  organType?: ConsigneeOrganType,
  isFgtsQueryEnabled = false,
  availableBalance?: number,
) => ({
  financedAmount: simulation.financed_amount,
  paymentAmount: simulation.payment_amount,
  formValues,
  organType,
  isFgtsQueryEnabled,
  availableBalance,
});

const showGuaranteeValidationErrors = (
  api: ReturnType<typeof notification.useNotification>[0],
  formValues: TLoanGuaranteeFormValues,
  validation: TGuaranteeFinancedValidationResult,
  financedAmount?: number,
) => {
  if (validation.isMarginRatioValid && validation.isFgtsGuaranteeSumValid) {
    return;
  }

  if (!validation.isMarginRatioValid) {
    if (shouldUseMarginWithFgtsCoverageValidation(formValues)) {
      api.error({
        message:
          'A soma das garantias informadas deve corresponder a 50% do valor do empréstimo.',
        duration: 10000,
        pauseOnHover: true,
      });
      return;
    }

    if (formValues.hasRescissionPenalty) {
      api.error({
        message: MARGIN_RATIO_ERROR_MESSAGE,
        duration: 10000,
        pauseOnHover: true,
      });
    }
  }

  if (
    !validation.isFgtsGuaranteeSumValid &&
    !shouldUseMarginWithFgtsCoverageValidation(formValues)
  ) {
    api.error({
      message: getFgtsGuaranteeSumErrorMessage(formValues, financedAmount),
      duration: 10000,
      pauseOnHover: true,
    });
  }
};

const SimulationV2Step = ({ isCalcSimulation }: SimulationV2StepProps) => {
  /* Hooks */
  const [api, context] = notification.useNotification();
  const theme = useTheme();
  const formRef = useRef<CreditSimulatorRefs>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    product,
    simulationV2,
    simulationV2Request,
    borrowerQualification,
    billetValidationResponse,
    qrcodeEmvValidationResponse,
    updateSimulationV2,
    onBack,
    onForward,
    consignedProduct,
    tacValidationError,
    borrowerData,
    getElegibilityRules,
    elegibilityError,
    isFgtsQueryEnabled,
    loanGuaranteeFormValues,
  } = useContext(ApplicationRequestContext);

  /* States */
  const [tabIndexSelected, setTabIndexSelected] = useState('');
  const [isValidElegibility, setIsValidElegibility] = useState(true);
  const [isValidMarginRatio, setIsValidMarginRatio] = useState(true);
  const [isValidFgtsGuaranteeSum, setIsValidFgtsGuaranteeSum] = useState(true);
  const [hasFirstPaymentDateFromForm, setHasFirstPaymentDateFromForm] =
    useState(false);

  /* Variables */
  const canCalculateCT =
    product?.organ_type !== ConsigneeOrganType.CT ||
    !!simulationV2Request?.firstPaymentDate ||
    hasFirstPaymentDateFromForm;
  const hasTimeValidation: boolean =
    product?.allow_disbursement_in_window || false;
  const isFgts = product?.organ_type === ConsigneeOrganType.FGTS;
  const isSiape =
    product?.organ_type === ConsigneeOrganType.SIAPE &&
    !!consignedProduct?.available_balance;
  const consignedAmount = consignedProduct?.available_balance || 0;
  const consignedParcels = 96;
  const consignedTotalAmount = consignedAmount * consignedParcels;

  // Validação SIAPE por tipo (parcela ou total)
  // e se o valor da parcela está menor que o valor liberado
  const isAmountAllowed =
    tabIndexSelected === '0'
      ? (consignedProduct?.available_balance || 0) >
        (simulationV2?.payment_amount || 0)
      : consignedTotalAmount >= (simulationV2?.requested_amount || 0);

  const isButtonDisabled =
    (isSiape && !isAmountAllowed) ||
    (!formRef.current?.isValid && !!simulationV2?.requested_amount) ||
    (product?.organ_type === ConsigneeOrganType.CT && !canCalculateCT) ||
    !!tacValidationError ||
    (isFgtsQueryEnabled &&
      !!simulationV2?.financed_amount &&
      !isValidMarginRatio) ||
    (isFgtsQueryEnabled &&
      !!simulationV2?.financed_amount &&
      !isValidFgtsGuaranteeSum);

  const applyGuaranteeFinancedValidation = useCallback(
    (
      formValues: TLoanGuaranteeFormValues,
      simulation = simulationV2,
      options?: { showErrors?: boolean },
    ) => {
      if (!simulation?.financed_amount) {
        return;
      }

      const validation = getGuaranteeFinancedValidation(
        getGuaranteeValidationParams(
          simulation,
          formValues,
          product?.organ_type as ConsigneeOrganType | undefined,
          isFgtsQueryEnabled,
          consignedProduct?.available_balance,
        ),
      );

      if (!validation) {
        return;
      }

      setIsValidMarginRatio(validation.isMarginRatioValid);
      setIsValidFgtsGuaranteeSum(validation.isFgtsGuaranteeSumValid);

      if (options?.showErrors && isFgtsQueryEnabled) {
        showGuaranteeValidationErrors(
          api,
          formValues,
          validation,
          simulation.financed_amount,
        );
      }
    },
    [
      api,
      consignedProduct?.available_balance,
      isFgtsQueryEnabled,
      product?.organ_type,
      simulationV2,
    ],
  );

  /* Handlers */
  const handleSimulator = () => {
    formRef.current?.simulate();
  };

  const handleRecalculate = () => {
    api.destroy();
    formRef.current?.simulate();
  };

  const handleContinue = async () => {
    const isGuaranteesValid = await formRef.current?.validateGuarantees();

    if (!isGuaranteesValid || !simulationV2?.financed_amount) {
      return;
    }

    const validation = getGuaranteeFinancedValidation(
      getGuaranteeValidationParams(
        simulationV2,
        loanGuaranteeFormValues,
        product?.organ_type as ConsigneeOrganType | undefined,
        isFgtsQueryEnabled,
        consignedProduct?.available_balance,
      ),
    );

    if (!validation) {
      return;
    }

    setIsValidMarginRatio(validation.isMarginRatioValid);
    setIsValidFgtsGuaranteeSum(validation.isFgtsGuaranteeSumValid);

    if (!validation.isMarginRatioValid || !validation.isFgtsGuaranteeSumValid) {
      applyGuaranteeFinancedValidation(loanGuaranteeFormValues, simulationV2, {
        showErrors: true,
      });
      return;
    }

    onForward();
  };

  const handleBack = () => {
    formRef.current?.reset();
    setIsValidMarginRatio(true);
    setIsValidFgtsGuaranteeSum(true);
    updateSimulationV2(null, null, '0'); // Reset simulation state
    onBack();
  };

  const getElegibilityRulesHandler = async (id: string) => {
    const data: IElegibility | null = await getElegibilityRules(id);

    if (data?.configs) {
      validateElegibility(data.configs);
    }
  };

  const validateElegibility = (configs: IElegibilityConfigs) => {
    if (configs) {
      const age = dayjs().diff(borrowerData?.birth_date, 'year');
      const education = borrowerData?.education_level;
      const installments = simulationV2?.num_periods;
      const operationValue = simulationV2?.total_amount_owed;
      const interestRate = simulationV2?.interest_rate;

      // Info: Lembrar de add tambem no useCreditSimulatorV2
      if (
        configs?.age &&
        (age < configs?.age?.min || age > configs?.age?.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Idade inválida',
          description: `A idade deve estar entre ${configs?.age?.min} e ${configs?.age?.max} anos.`,
          duration: 10000,
          pauseOnHover: true,
        });
      }

      if (
        !!education &&
        !configs?.education?.allowedDegrees.includes(education)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Grau de instrução inválido',
          duration: 10000,
          description:
            'O grau de escolaridade informado não permite a contratação deste produto',
        });
      }

      if (
        installments &&
        configs?.installments &&
        (installments < configs?.installments.min ||
          installments > configs?.installments.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Parcelas inválidas',
          duration: 10000,
          description: `O número de parcelas deve estar entre ${configs?.installments.min} e ${configs?.installments.max}.`,
        });
      }

      if (
        interestRate &&
        configs?.interestRate &&
        (interestRate < configs?.interestRate.min ||
          interestRate > configs?.interestRate.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Taxa de juros inválida',
          duration: 10000,
          description: `A taxa de juros deve estar entre ${configs?.interestRate.min} e ${configs?.interestRate.max}.`,
        });
      }

      if (
        operationValue &&
        configs?.operationValue &&
        (operationValue < configs?.operationValue.min ||
          operationValue > configs?.operationValue.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Valor de operação inválido',
          duration: 10000,
          description: `O valor da operação deve estar entre ${formatCurrency(
            configs?.operationValue.min,
          )} e ${formatCurrency(configs?.operationValue.max)}.`,
        });
      }
    }
  };

  useEffect(() => {
    if (product?.id) {
      getElegibilityRulesHandler(product?.id);
    }
  }, [product?.id]);

  useEffect(() => {
    if (!isFgtsQueryEnabled || !simulationV2?.financed_amount) {
      return;
    }

    applyGuaranteeFinancedValidation(loanGuaranteeFormValues);
  }, [
    applyGuaranteeFinancedValidation,
    isFgtsQueryEnabled,
    loanGuaranteeFormValues,
    simulationV2?.financed_amount,
    simulationV2?.payment_amount,
  ]);

  useEffect(() => {
    if (!simulationV2?.requested_amount) {
      setIsValidMarginRatio(true);
      setIsValidFgtsGuaranteeSum(true);
    }
  }, [simulationV2?.requested_amount]);

  useEffect(() => {
    if (elegibilityError?.message) {
      api.error({
        message: elegibilityError.message,
        description: elegibilityError?.description || '',
        duration: 10000,
        pauseOnHover: true,
      });
    }
  }, [elegibilityError]);

  // FORA DE ESCOPO
  //const downloadSimulationHandler = useCallback(async () => {
  //  const file = await downloadSimulation(consignedId);
  //
  //  if (file) {
  //    saveAs(
  //      new Blob([file], {
  //        type: 'application/pdf',
  //      }),
  //      `simulacao_${consignedId}.pdf`,
  //    );
  //  }
  //}, [consignedId]);

  return (
    <BoxSection>
      {context}
      <SimulationStepContainer ref={containerRef}>
        <CreditSimulator
          ref={formRef}
          product={product}
          simulationResponse={simulationV2}
          simulationRequest={simulationV2Request}
          billetValidationResponse={billetValidationResponse}
          qualification={borrowerQualification}
          qrcodeEmvValidationResponse={qrcodeEmvValidationResponse}
          hasTimeValidation={hasTimeValidation}
          onCTFirstPaymentDateChange={setHasFirstPaymentDateFromForm}
          onGuaranteeFinancedValidation={(formValues, options) =>
            applyGuaranteeFinancedValidation(formValues, simulationV2, options)
          }
          onSimulate={(
            simulation,
            simulationRequest,
            tabIndex,
            loanGuaranteeFormValuesFromSimulate,
          ) => {
            setTabIndexSelected(tabIndex);

            const guaranteeFormValues =
              loanGuaranteeFormValuesFromSimulate ??
              defaultLoanGuaranteeFormValues;

            applyGuaranteeFinancedValidation(guaranteeFormValues, simulation, {
              showErrors: true,
            });

            updateSimulationV2(simulation, simulationRequest, tabIndex);
            setTimeout(() =>
              containerRef.current?.scrollTo({
                top: 9999,
                behavior: 'smooth',
              }),
            );
          }}
        />
      </SimulationStepContainer>
      <ButtonsArea>
        <div>
          {!isFgts && (
            <ButtonDefault
              style={{
                maxWidth: '200px',
              }}
              onClick={() => formRef.current?.reset()}
            >
              <TextL style={{ color: theme.palette.brand.primary.base }}>
                Limpar condições
              </TextL>
            </ButtonDefault>
          )}
        </div>
        <div className="btn-preview">
          {!isCalcSimulation && (
            <ButtonDefault onClick={handleBack}>
              <ArrowBack />
            </ButtonDefault>
          )}
          {simulationV2 && simulationV2.requested_amount && (
            <>
              <ButtonDefault onClick={() => formRef.current?.copyPreview()}>
                <TextL
                  style={{
                    color: theme.palette.brand.primary.base,
                    minWidth: '200px',
                  }}
                >
                  Copiar resultados
                </TextL>
              </ButtonDefault>
              {!isCalcSimulation && (
                <ButtonDefault
                  style={{ color: theme.palette.brand.primary.base }}
                  onClick={handleRecalculate}
                >
                  <TextL style={{ color: theme.palette.brand.primary.base }}>
                    Recalcular
                  </TextL>
                </ButtonDefault>
              )}
            </>
          )}
          {isCalcSimulation ? (
            <ButtonPrimary
              disabled={isButtonDisabled || !isValidElegibility}
              onClick={
                simulationV2?.requested_amount
                  ? handleRecalculate
                  : handleSimulator
              }
            >
              <TextL style={{ color: theme.palette.common.white }}>
                {!simulationV2?.requested_amount ? 'Calcular' : 'Recalcular'}
              </TextL>
            </ButtonPrimary>
          ) : (
            <ButtonPrimary
              disabled={isButtonDisabled || !isValidElegibility}
              onClick={
                !simulationV2?.requested_amount
                  ? handleSimulator
                  : handleContinue
              }
            >
              <TextL style={{ color: theme.palette.common.white }}>
                {!simulationV2?.requested_amount ? 'Calcular' : 'Avançar'}
              </TextL>
            </ButtonPrimary>
          )}
        </div>
      </ButtonsArea>
    </BoxSection>
  );
};

export default SimulationV2Step;
